/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CBM.BREAKERS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/CbmBreakersPage.tsx. A DISTINCT
 * presentation (KPI tiles + segmented card lists + bottom-sheet detail) over the
 * SAME data/logic layer as the desktop page — never the desktop grid re-hosted (§16):
 *   - reads via apiDb: cbm_service (breaker config), the cbm_overview RPC (live
 *     per-service state + sliding-window stats) merged by service_code, and
 *     cbm_transition_log (recent state changes),
 *   - each transition's cbm_service_id is resolved to the service name for display —
 *     a raw id never reaches render (§17.B4),
 *   - "Reset" and "Trip" call the module's own control RPCs (cbm_reset / cbm_trip),
 *     exactly as the desktop page does — that is CBM's sanctioned path for breaker
 *     state (operational middleware state, not business data, so it does not funnel
 *     through the Common Routine). No direct table writes happen here.
 * Breaker state and Y/N render as status pills; the fallback-strategy and state
 * CHECK-enums use i18n labelled constants (never a bare literal); telemetry
 * timestamps use the locale-aware formatter (§11).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Inbox, X, RefreshCw, ZapOff, Activity, ArrowRightLeft,
} from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Row shapes — column names verified against the desktop CbmBreakersPage query.
// ---------------------------------------------------------------------------

type BreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

/** cbm_service — the persisted breaker configuration. */
interface ServiceRow {
  cbm_service_id: number;
  service_code: string;
  name: string;
  window_seconds: number;
  min_volume: number;
  failure_rate_threshold: number;
  timeout_ms: number;
  open_cooldown_seconds: number;
  half_open_max_trials: number;
  fallback_strategy: string;
  is_active: 'Y' | 'N';
}

/** cbm_overview RPC — the live runtime state + sliding-window telemetry. */
interface Overview {
  service_code: string;
  state: BreakerState;
  opened_at: string | null;
  next_probe_at: string | null;
  last_transition_at: string | null;
  window_total: number;
  failure_rate: number;
}

/** cbm_transition_log — the breaker state-change telemetry feed. */
interface TransRow {
  cbm_transition_log_id: number;
  cbm_service_id: number;
  from_state: string | null;
  to_state: string;
  reason: string | null;
  failure_rate: number | null;
  sample_size: number | null;
  at: string;
}

/** Config merged with live overview — the mobile card/detail model. */
interface BreakerRow extends ServiceRow {
  state: BreakerState;
  opened_at: string | null;
  next_probe_at: string | null;
  last_transition_at: string | null;
  window_total: number;
  failure_rate: number;
}

type ActiveView = 'breakers' | 'transitions';
type StateFacet = 'all' | BreakerState;
type ActiveFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labelled constants (§17.B11) — the stored value stays the code; the
// displayed label is localized via t(). Never a bare literal in the JSX.
const STATES = [
  { value: 'CLOSED', label: 'Closed' },
  { value: 'OPEN', label: 'Open' },
  { value: 'HALF_OPEN', label: 'Half-open' },
] as const;

const FALLBACKS = [
  { value: 'EXCEPTION', label: 'Throw exception' },
  { value: 'LAST_CACHED', label: 'Last cached value' },
  { value: 'STATIC_JSON', label: 'Static JSON' },
] as const;

function statePillTone(s: BreakerState): MobilePillTone {
  return s === 'CLOSED' ? 'ok' : s === 'OPEN' ? 'no' : 'wait';
}
function stateLeadTone(s: BreakerState): MobileLeadTone {
  return s === 'CLOSED' ? 'green' : s === 'OPEN' ? 'red' : 'amber';
}
function pct(fraction: number): number {
  return Math.round((fraction ?? 0) * 100);
}

export function MobileCbmBreakersPage({ session: _session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();

  const [breakers, setBreakers] = useState<BreakerRow[]>([]);
  const [trans, setTrans] = useState<TransRow[]>([]);

  const [view, setView] = useState<ActiveView>('breakers');
  const [stateFacet, setStateFacet] = useState<StateFacet>('all');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<BreakerRow | null>(null);
  const [selectedTrans, setSelectedTrans] = useState<TransRow | null>(null);

  const stateLabel = (value: string): string => {
    const def = STATES.find((s) => s.value === value);
    return def ? t('mobile.cbm.state.' + def.value, def.label) : value.replace('_', '-');
  };
  const fallbackLabel = (value: string): string => {
    const def = FALLBACKS.find((f) => f.value === value);
    return def ? t('mobile.cbm.fallback.' + def.value, def.label) : value;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [s, o, tr] = await Promise.all([
        apiDb
          .from('cbm_service')
          .select('cbm_service_id, service_code, name, window_seconds, min_volume, failure_rate_threshold, timeout_ms, open_cooldown_seconds, half_open_max_trials, fallback_strategy, is_active')
          .order('service_code'),
        apiDb.rpc<Overview[]>('cbm_overview'),
        apiDb
          .from('cbm_transition_log')
          .select('cbm_transition_log_id, cbm_service_id, from_state, to_state, reason, failure_rate, sample_size, at')
          .order('at', { ascending: false })
          .limit(50),
      ]);
      if (s.error) throw s.error;

      const overviews = (Array.isArray(o.data) ? o.data : []) as Overview[];
      const ovByCode = new Map<string, Overview>();
      for (const ov of overviews) ovByCode.set(ov.service_code, ov);

      const rows: BreakerRow[] = ((s.data ?? []) as ServiceRow[]).map((svc) => {
        const ov = ovByCode.get(svc.service_code);
        return {
          ...svc,
          state: ov?.state ?? 'CLOSED',
          opened_at: ov?.opened_at ?? null,
          next_probe_at: ov?.next_probe_at ?? null,
          last_transition_at: ov?.last_transition_at ?? null,
          window_total: ov?.window_total ?? 0,
          failure_rate: ov?.failure_rate ?? 0,
        };
      });
      setBreakers(rows);
      setTrans((tr.data ?? []) as TransRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Mount only — service names are plain columns (no _tl), so no locale-driven
  // relabel is needed; telemetry dates format themselves via the locale hook.
  useEffect(() => { void reload(); }, []);

  // Resolve a transition's cbm_service_id to its service name (§17.B4 — never the id).
  const serviceName = (id: number): string =>
    breakers.find((b) => b.cbm_service_id === id)?.name ?? t('common.none', 'None');

  const openCount = useMemo(() => breakers.filter((b) => b.state === 'OPEN').length, [breakers]);
  const halfOpenCount = useMemo(() => breakers.filter((b) => b.state === 'HALF_OPEN').length, [breakers]);

  const filteredBreakers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return breakers.filter((b) => {
      if (activeFacet === 'active' && b.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && b.is_active !== 'N') return false;
      if (stateFacet !== 'all' && b.state !== stateFacet) return false;
      if (!q) return true;
      return (
        b.name.toLowerCase().includes(q) ||
        b.service_code.toLowerCase().includes(q) ||
        stateLabel(b.state).toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakers, filter, stateFacet, activeFacet]);

  const filteredTrans = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return trans;
    return trans.filter((tr) =>
      serviceName(tr.cbm_service_id).toLowerCase().includes(q) ||
      stateLabel(tr.to_state).toLowerCase().includes(q) ||
      (tr.reason ?? '').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trans, filter, breakers]);

  // CBM control RPCs — the module's sanctioned path for breaker state, exactly as
  // the desktop page's act() does (not a business write → not the Common Routine).
  const control = async (fn: 'cbm_reset' | 'cbm_trip', code: string) => {
    setActionBusy(true);
    setError(null);
    const { error: e } = await apiDb.rpc(fn, { p_service_code: code });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const views: { key: ActiveView; label: string }[] = [
    { key: 'breakers', label: t('mobile.cbm.view.breakers', 'Breakers') },
    { key: 'transitions', label: t('mobile.cbm.view.transitions', 'Transitions') },
  ];

  const stateFacets: { key: StateFacet; label: string }[] = [
    { key: 'all', label: t('mobile.cbm.state_all', 'All') },
    ...STATES.map((s) => ({ key: s.value as StateFacet, label: stateLabel(s.value) })),
  ];

  const activeFacets: { key: ActiveFacet; label: string }[] = [
    { key: 'all', label: t('mobile.cbm.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const searchPlaceholder =
    view === 'breakers'
      ? t('mobile.cbm.search.breakers', 'Search services')
      : t('mobile.cbm.search.transitions', 'Search transitions');

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.cbm.title', 'Circuit Breakers')}
        description={t('mobile.cbm.description', 'Live breaker state and recent transitions across protected services.')}
      />

      <MobileStatStrip
        stats={[
          { value: breakers.length, label: t('mobile.cbm.kpi.services', 'Services') },
          { value: openCount, label: t('mobile.cbm.kpi.open', 'Open') },
          { value: halfOpenCount, label: t('mobile.cbm.kpi.half_open', 'Half-open') },
          { value: trans.length, label: t('mobile.cbm.kpi.transitions', 'Transitions') },
        ]}
      />

      {/* View selector */}
      <MobileFilterChips
        chips={views.map((vw) => ({ value: vw.key, label: vw.label }))}
        isActive={(v) => view === v}
        onSelect={(v) => setView(v as ActiveView)}
      />

      {/* State facets (breakers view only) */}
      {view === 'breakers' && (
        <MobileFilterChips
          chips={stateFacets.map((f) => ({ value: f.key, label: f.label }))}
          isActive={(v) => stateFacet === v}
          onSelect={(v) => setStateFacet(v as StateFacet)}
        />
      )}

      {/* Active-status facets (breakers view only) — independent of state facets */}
      {view === 'breakers' && (
        <MobileFilterChips
          chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
          isActive={(v) => activeFacet === v}
          onSelect={(v) => setActiveFacet(v as ActiveFacet)}
        />
      )}

      {/* Search */}
      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
      </div>

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {/* Breakers list */}
      {view === 'breakers' && (
        filteredBreakers.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.cbm.no_breakers', 'No services match')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredBreakers.map((b) => (
              <MobileCardRow
                key={b.cbm_service_id}
                lead={<Activity />}
                leadTone={stateLeadTone(b.state)}
                title={b.name}
                subtitle={
                  `${b.service_code} · ${t('mobile.cbm.reqs', '{{n}} reqs', { n: b.window_total })}` +
                  ` · ${t('mobile.cbm.fail_pct', '{{pct}}% fail', { pct: pct(b.failure_rate) })}`
                }
                trailing={
                  <>
                    <MobileStatusPill tone={b.is_active === 'Y' ? 'ok' : 'no'}>
                      {b.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                    <MobileStatusPill tone={statePillTone(b.state)}>{stateLabel(b.state)}</MobileStatusPill>
                  </>
                }
                chevron
                onClick={() => setSelected(b)}
              />
            ))}
          </MobileContentCard>
        )
      )}

      {/* Transitions list */}
      {view === 'transitions' && (
        filteredTrans.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.cbm.no_transitions', 'No transitions yet')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredTrans.map((tr) => (
              <MobileCardRow
                key={tr.cbm_transition_log_id}
                lead={<ArrowRightLeft />}
                leadTone={stateLeadTone(tr.to_state as BreakerState)}
                title={serviceName(tr.cbm_service_id)}
                subtitle={
                  `${tr.from_state ? stateLabel(tr.from_state) : t('common.none', 'None')} → ${stateLabel(tr.to_state)}` +
                  ` · ${formatDateTime(tr.at)}`
                }
                trailing={<MobileStatusPill tone={statePillTone(tr.to_state as BreakerState)}>{stateLabel(tr.to_state)}</MobileStatusPill>}
                chevron
                onClick={() => setSelectedTrans(tr)}
              />
            ))}
          </MobileContentCard>
        )
      )}

      {/* Breaker detail + control bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.cbm.detail', 'Breaker detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden><Activity size={20} /></span>
              <span>
                <div className="m-nm">{selected.name}</div>
                <div className="m-em">{selected.service_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.cbm.field.state', 'State')}
                value={<MobileStatusPill tone={statePillTone(selected.state)}>{stateLabel(selected.state)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.cbm.field.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.cbm.field.window_now', 'Window now')}
                value={
                  `${t('mobile.cbm.reqs', '{{n}} reqs', { n: selected.window_total })}` +
                  ` · ${t('mobile.cbm.fail_pct', '{{pct}}% fail', { pct: pct(selected.failure_rate) })}`
                }
              />
              <MobileDetailRow label={t('mobile.cbm.field.fallback', 'Fallback')} value={fallbackLabel(selected.fallback_strategy)} />
              <MobileDetailRow
                label={t('mobile.cbm.field.thresholds', 'Thresholds')}
                value={t('mobile.cbm.threshold_summary', '{{pct}}% over {{vol}} in {{win}}s', {
                  pct: pct(selected.failure_rate_threshold),
                  vol: selected.min_volume,
                  win: selected.window_seconds,
                })}
              />
              <MobileDetailRow label={t('mobile.cbm.field.timeout', 'Timeout')} value={t('mobile.cbm.ms', '{{n}} ms', { n: selected.timeout_ms })} />
              <MobileDetailRow label={t('mobile.cbm.field.cooldown', 'Open cooldown')} value={t('mobile.cbm.seconds', '{{n}}s', { n: selected.open_cooldown_seconds })} />
              <MobileDetailRow label={t('mobile.cbm.field.canaries', 'Half-open canaries')} value={selected.half_open_max_trials} />
              {selected.state === 'OPEN' && selected.next_probe_at && (
                <MobileDetailRow label={t('mobile.cbm.field.next_probe', 'Next probe')} value={formatDateTime(selected.next_probe_at)} />
              )}
              {selected.opened_at && (
                <MobileDetailRow label={t('mobile.cbm.field.opened_at', 'Opened')} value={formatDateTime(selected.opened_at)} />
              )}
              {selected.last_transition_at && (
                <MobileDetailRow label={t('mobile.cbm.field.last_transition', 'Last transition')} value={formatDateTime(selected.last_transition_at)} />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy || selected.state === 'CLOSED'}
                onClick={() => void control('cbm_reset', selected.service_code)}
              >
                <RefreshCw size={16} />
                {t('mobile.cbm.action.reset', 'Reset (close)')}
              </button>
              <button
                type="button"
                className="m-btn m-btn-danger"
                disabled={actionBusy || selected.state === 'OPEN'}
                onClick={() => void control('cbm_trip', selected.service_code)}
              >
                <ZapOff size={16} />
                {t('mobile.cbm.action.trip', 'Trip (open)')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Transition detail bottom-sheet (read-only telemetry record) */}
      <div className={'m-scrim' + (selectedTrans ? ' m-open' : '')} onClick={() => setSelectedTrans(null)} />
      <div className={'m-sheet' + (selectedTrans ? ' m-open' : '')} aria-label={t('mobile.cbm.transition_detail', 'Transition detail')}>
        <div className="m-grabber" />
        {selectedTrans && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden><ArrowRightLeft size={20} /></span>
              <span>
                <div className="m-nm">{serviceName(selectedTrans.cbm_service_id)}</div>
                <div className="m-em">{formatDateTime(selectedTrans.at)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.cbm.field.transition', 'Transition')}
                value={`${selectedTrans.from_state ? stateLabel(selectedTrans.from_state) : t('common.none', 'None')} → ${stateLabel(selectedTrans.to_state)}`}
              />
              <MobileDetailRow label={t('mobile.cbm.field.reason', 'Reason')} value={selectedTrans.reason ?? '—'} />
              <MobileDetailRow
                label={t('mobile.cbm.field.fail_rate', 'Failure rate')}
                value={selectedTrans.failure_rate != null ? t('mobile.cbm.fail_pct', '{{pct}}% fail', { pct: pct(selectedTrans.failure_rate) }) : '—'}
              />
              <MobileDetailRow label={t('mobile.cbm.field.sample_size', 'Sample size')} value={selectedTrans.sample_size ?? '—'} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelectedTrans(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
